import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import { CustomNotifierService } from './custom-notifier.service';
import { AutoRefreshManagerService } from './auto-refresh-manager.service';
import { AppConstants } from '../constants/constants';

export interface InactivityConfig {
  inactivityTimeoutMs: number;
  warningTimeoutMs: number;
  checkIntervalMs: number;
}

export interface InactivityState {
  isWarningVisible: boolean;
  secondsRemaining: number;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class InactivityService implements OnDestroy {
  private readonly DEFAULT_CONFIG: InactivityConfig = {
    inactivityTimeoutMs: 10 * 60 * 1000, // 10 minutes
    warningTimeoutMs: 1 * 60 * 1000,    // 1 minute warning
    checkIntervalMs: 5000                // Check every 5 seconds (optimized)
  };

  private config: InactivityConfig = this.DEFAULT_CONFIG;
  private lastActivity = Date.now();
  private readonly destroy$ = new Subject<void>();
  private countdownDestroy$ = new Subject<void>(); // Separate subject for countdown

  // Memory-safe event handlers
  private readonly boundActivityHandler = this.onUserActivity.bind(this);
  private readonly boundVisibilityHandler = this.handleVisibilityChange.bind(this);
  private readonly boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this);
  private readonly boundStorageHandler = this.handleCrossTabActivity.bind(this);

  // State observables
  private readonly inactivityState$ = new BehaviorSubject<InactivityState>({
    isWarningVisible: false,
    secondsRemaining: 0,
    isActive: false
  });

  // Events to monitor for user activity (optimized for mobile and desktop)
  private readonly ACTIVITY_EVENTS = [
    'mousedown', 'mousemove', 'keypress', 'keydown', 'keyup',
    'scroll', 'touchstart', 'touchmove', 'touchend', 'click', 'contextmenu',
    'dblclick', 'mousewheel', 'DOMMouseScroll', 'wheel', 'focus', 'blur'
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly _customNotifierService: CustomNotifierService,
    private readonly autoRefreshManager: AutoRefreshManagerService
  ) { }

  /**
   * Initialize the inactivity service with memory-safe event handling
   */
  startTracking(): void {
    this.resetTimer();
    this.bindActivityEvents();
    this.bindBrowserEvents();
    this.bindCrossTabEvents();
    this.startInactivityCheck();
    this.updateState({ isActive: true });
  }

  /**
   * Stop tracking user activity with complete cleanup
   */
  stopTracking(): void {
    this.unbindActivityEvents();
    this.unbindBrowserEvents();
    this.unbindCrossTabEvents();
    this.clearTimers();
    this.updateState({
      isActive: false,
      isWarningVisible: false,
      secondsRemaining: 0
    });
  }

  /**
   * Get the current inactivity state
   */
  getState(): Observable<InactivityState> {
    return this.inactivityState$.asObservable();
  }

  /**
   * Extend the session (reset timer)
   */
  extendSession(): void {
    this.resetTimer();
    this.clearCountdown();
    this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<InactivityConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Bind activity event listeners with memory-safe handlers
   */
  private bindActivityEvents(): void {
    this.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, this.boundActivityHandler, { passive: true, capture: true });
    });
  }

  /**
   * Unbind activity event listeners
   */
  private unbindActivityEvents(): void {
    this.ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, this.boundActivityHandler, true);
    });
  }

  /**
   * Bind browser-level event listeners
   */
  private bindBrowserEvents(): void {
    document.addEventListener('visibilitychange', this.boundVisibilityHandler, { passive: true });
    globalThis.addEventListener('beforeunload', this.boundBeforeUnloadHandler, { passive: true });
    globalThis.addEventListener('pagehide', this.boundBeforeUnloadHandler, { passive: true });
  }

  /**
   * Unbind browser-level event listeners
   */
  private unbindBrowserEvents(): void {
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    globalThis.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
    globalThis.removeEventListener('pagehide', this.boundBeforeUnloadHandler);
  }

  /**
   * Bind cross-tab communication events
   */
  private bindCrossTabEvents(): void {
    globalThis.addEventListener('storage', this.boundStorageHandler, { passive: true });
  }

  /**
   * Unbind cross-tab communication events
   */
  private unbindCrossTabEvents(): void {
    globalThis.removeEventListener('storage', this.boundStorageHandler);
  }

  /**
   * Handle user activity
   */
  private onUserActivity(): void {
    this.resetTimer();

    // Broadcast activity to other tabs
    localStorage.setItem(AppConstants.PRISISTED_KEYS.USER_ACTIVITY_TIMESTAMP, Date.now().toString());

    // Hide warning if it's currently showing
    if (this.inactivityState$.value.isWarningVisible) {
      this.clearCountdown();
      this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
    }
  }

  /**
   * Handle cross-tab activity communication
   */
  private handleCrossTabActivity(e: StorageEvent): void {
    if (e.key === AppConstants.PRISISTED_KEYS.USER_ACTIVITY_TIMESTAMP && e.newValue) {
      const timestamp = Number.parseInt(e.newValue, 10);
      if (timestamp > this.lastActivity) {
        this.lastActivity = timestamp;

        // Clear warning if showing due to other tab activity
        if (this.inactivityState$.value.isWarningVisible) {
          this.clearCountdown();
          this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
        }
      }
    }
  }

  /**
   * Handle browser visibility changes (reset timer when user returns)
   */
  private handleVisibilityChange(): void {
    if (!document.hidden) {
      // User returned to tab - this shows activity, so reset timer
      this.resetTimer();

      // Broadcast activity to other tabs
      localStorage.setItem(AppConstants.PRISISTED_KEYS.USER_ACTIVITY_TIMESTAMP, Date.now().toString());

      // Clear any warning that might be showing
      if (this.inactivityState$.value.isWarningVisible) {
        this.clearCountdown();
        this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
      }
    }
  }

  /**
   * Handle page unload/close
   */
  private handleBeforeUnload(): void {
    this.stopTracking();
  }

  /**
   * Reset the activity timer
   */
  private resetTimer(): void {
    this.lastActivity = Date.now();
  }

  /**
   * Start the inactivity check interval with RxJS for better memory management
   */
  private startInactivityCheck(): void {
    this.ngZone.runOutsideAngular(() => {
      // Use RxJS timer for better memory management
      timer(0, this.config.checkIntervalMs)
        .pipe(
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.checkInactivity();
        });
    });
  }

  /**
   * Check for user inactivity (continues normally in background)
   * Paused when auto-refresh is active to prevent false timeouts
   */
  private checkInactivity(): void {
    // Pause inactivity checking when any auto-refresh is active
    if (this.autoRefreshManager.isAnyAutoRefreshActive()) {
      // Reset activity timer when auto-refresh is active (keeps session fresh)
      this.resetTimer();

      // Hide any warning that might be showing
      if (this.inactivityState$.value.isWarningVisible) {
        this.ngZone.run(() => {
          this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
        });
      }
      return; // Skip inactivity check while auto-refresh is active
    }

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    const warningThreshold = this.config.inactivityTimeoutMs - this.config.warningTimeoutMs;

    // Hide warning UI when tab is hidden (timer continues normally)
    if (document.hidden && this.inactivityState$.value.isWarningVisible) {
      this.ngZone.run(() => {
        this.updateState({ isWarningVisible: false, secondsRemaining: 0 });
      });
    }

    // Show warning when threshold is reached (only if tab is visible)
    if (timeSinceLastActivity >= warningThreshold &&
      !this.inactivityState$.value.isWarningVisible &&
      !document.hidden) {
      this.ngZone.run(() => {
        this.startCountdown();
      });
    }

    // Auto logout when timeout is reached (works in background tabs)
    if (timeSinceLastActivity >= this.config.inactivityTimeoutMs) {
      this.ngZone.run(() => {
        this.performAutoLogout();
      });
    }
  }

  /**
   * Start the countdown timer with proper reset capability
   */
  private startCountdown(): void {
    const warningSeconds = Math.floor(this.config.warningTimeoutMs / 1000);
    let secondsRemaining = warningSeconds;

    this.updateState({
      isWarningVisible: true,
      secondsRemaining: secondsRemaining
    });

    // Create new subject for this countdown session
    this.countdownDestroy$ = new Subject<void>();

    // Use RxJS timer for countdown with dedicated destroy subject
    timer(0, 1000)
      .pipe(
        takeUntil(this.countdownDestroy$)
      )
      .subscribe(() => {
        secondsRemaining--;
        this.updateState({ secondsRemaining: secondsRemaining });

        if (secondsRemaining <= 0) {
          this.clearCountdown();
          this.performAutoLogout();
        }
      });
  }

  /**
   * Clear the countdown timer
   */
  private clearCountdown(): void {
    // Stop the countdown subscription
    this.countdownDestroy$.next();
    this.countdownDestroy$.complete();

    this.updateState({
      isWarningVisible: false,
      secondsRemaining: 0
    });
  }

  /**
   * Clear all timers (RxJS handles cleanup automatically)
   */
  private clearTimers(): void {
    // Clear countdown first
    this.clearCountdown();

    // Trigger destroy to stop all subscriptions
    this.destroy$.next();
  }

  /**
   * Perform automatic logout
   */
  private performAutoLogout(): void {
    this.stopTracking();

    // Get current language for notification message
    const language = localStorage.getItem('language') === 'en' ? 'en' : 'ar';
    const message = language === 'en'
      ? 'Your session has expired. Please sign in again.'
      : 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.';

    // Sign out the user
    this.authService.signOut().subscribe(() => {
      // Clear any remaining state
      this.updateState({
        isActive: false,
        isWarningVisible: false,
        secondsRemaining: 0
      });

      // Show unauthorized notification (same as auth interceptor)
      this._customNotifierService.notify('error', message);

      // Navigate to login page
      this.router.navigate(['/account/signin']);
    });
  }

  /**
   * Update the inactivity state
   */
  private updateState(partialState: Partial<InactivityState>): void {
    const currentState = this.inactivityState$.value;
    const newState = { ...currentState, ...partialState };
    this.inactivityState$.next(newState);
  }

  /**
   * Cleanup on service destruction with complete memory management
   */
  ngOnDestroy(): void {
    this.stopTracking();
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up countdown subject if it exists
    if (!this.countdownDestroy$.closed) {
      this.countdownDestroy$.next();
      this.countdownDestroy$.complete();
    }

    // Additional cleanup for memory safety
    this.inactivityState$.complete();
  }
}
